import { useEffect, useMemo, useState } from "react";

import { EFFECT_DRAG_MIME, EFFECT_PRESETS, type EffectPreset } from "../../lib/effectPresets";
import { useEditor } from "../../store/editor";
import EffectThumb from "./EffectThumb";

function uid(): string {
  return Math.random().toString(36).slice(2, 9);
}

/** Aplica un preset a un clip: reemplaza sus efectos. */
export function applyEffectPresetToClip(clipId: string, preset: EffectPreset) {
  const tl = useEditor.getState().timeline;
  const effects = preset.build(uid);
  const tracks = tl.tracks.map((t) => ({
    ...t,
    clips: t.clips.map((c) => c.id === clipId ? { ...c, effects } : c),
  }));
  useEditor.getState().setTimeline({ ...tl, tracks });
}

export default function EffectsPanel(props: { onClose: () => void }) {
  const [query, setQuery] = useState("");
  const selectedIds = useEditor((s) => s.selectedClipIds);
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") props.onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props]);

  const groups = useMemo(() => {
    const filtered = EFFECT_PRESETS.filter((p) => {
      if (!query) return true;
      const q = query.toLowerCase();
      return p.label.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q);
    });
    const map = new Map<string, EffectPreset[]>();
    for (const p of filtered) {
      if (!map.has(p.category)) map.set(p.category, []);
      map.get(p.category)!.push(p);
    }
    return Array.from(map.entries());
  }, [query]);

  function onDragStart(e: React.DragEvent, preset: EffectPreset) {
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData(EFFECT_DRAG_MIME, JSON.stringify({ id: preset.id }));
    e.dataTransfer.setData(`${EFFECT_DRAG_MIME}+id`, preset.id);
  }

  function onClickPreset(p: EffectPreset) {
    if (!selectedId) {
      alert("Selecciona primero un clip en la timeline.");
      return;
    }
    applyEffectPresetToClip(selectedId, p);
  }

  return (
    <div style={{
      position: "fixed", left: 0, top: 56, bottom: 0,
      width: 420, zIndex: 9000, pointerEvents: "none",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "100%", height: "100%",
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        boxShadow: "8px 0 30px rgba(0,0,0,0.4)",
        padding: 14, overflow: "hidden",
        display: "flex", flexDirection: "column",
        pointerEvents: "auto",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            Efectos <span style={{ opacity: 0.5, fontSize: 12 }}>({EFFECT_PRESETS.length})</span>
          </h3>
          <input
            placeholder="Buscar…"
            value={query} onChange={(e) => setQuery(e.target.value)}
            style={{ flex: 1, maxWidth: 200 }}
            autoFocus
          />
          <button onClick={props.onClose} style={{ fontSize: 12 }}>×</button>
        </div>

        {!selectedId && (
          <div style={{
            background: "rgba(251,146,60,0.15)",
            border: "1px solid #fb923c",
            borderRadius: 6, padding: 8, marginBottom: 10,
            fontSize: 11, color: "var(--text-dim)",
          }}>
            Selecciona un clip de vídeo para aplicar un efecto. O arrastra un efecto sobre un clip.
          </div>
        )}

        <div style={{ overflowY: "auto", flex: 1 }}>
          {groups.map(([catName, items]) => (
            <section key={catName} style={{ marginBottom: 14 }}>
              <h4 style={{ margin: "0 0 6px", fontSize: 11, color: "var(--text-dim)",
                            textTransform: "uppercase", letterSpacing: 0.05 }}>
                {catName}
              </h4>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))",
                gap: 8,
              }}>
                {items.map((p) => (
                  <EffectThumb
                    key={p.id}
                    effects={p.build(() => Math.random().toString(36).slice(2, 9))}
                    label={p.label}
                    icon={p.icon}
                    size={80}
                    draggable
                    onDragStart={(e) => onDragStart(e, p)}
                    onPick={() => onClickPreset(p)}
                    title={p.desc}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div style={{ borderTop: "1px solid var(--border)", paddingTop: 8, fontSize: 11,
                       color: "var(--text-dim)" }}>
          💡 Click = aplicar al clip seleccionado · Arrastra = aplicar a clip específico
        </div>
      </div>
    </div>
  );
}
